import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { loginStyles as s } from '../../styles/loginStyles';
import ForgotPassword from './ForgotPassword';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import logoImg from '../../assets/logo.png';
import authImg from '../../assets/auth-image.jpg';

const Login = ({ forcedView }) => {
  const { isRecovery, setIsRecovery } = useAuth();
  const [view, setView] = useState(forcedView || 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const navigate = useNavigate();

  const mapError = (error) => {
    if (!error) return null;
    const msg = error.message.toLowerCase();
    if (msg.includes('invalid login credentials')) return 'Correo o contraseña incorrectos.';
    if (msg.includes('email not confirmed')) return 'Debes confirmar tu correo electrónico.';
    if (msg.includes('user not found')) return 'El usuario no existe.';
    return 'Ocurrió un error. Inténtalo de nuevo.';
  };

  useEffect(() => {
    if (isRecovery) setView('update');
  }, [isRecovery]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setErrorMessage(mapError(error));
    } else {
      navigate('/main');
    }
    setLoading(false);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    setErrorMessage('');
    
    const { error } = await supabase.auth.updateUser({ 
      password,
      data: { password_set: true } 
    });

    if (error) {
      setErrorMessage('No se pudo actualizar la contraseña. El enlace puede haber expirado.');
      setLoading(false);
    } else {
      setSuccess(true);
      setIsRecovery(false);
      await supabase.auth.signOut();
      setTimeout(() => { 
        window.location.replace('/login');
      }, 2000);
    }
  };

  return (
    <div className={s.container}>
      <div className={s.formWrapper}>
        <div className="w-full max-w-[440px] animate-in fade-in duration-500">
          <div className={s.headerWrapper}>
            <img src={logoImg} alt="MSC Rewards" className={s.logo} />
            <h1 className={s.title}>
              {view === 'update' ? "Establecer Contraseña" : "Bienvenido de nuevo"}
            </h1>
          </div>

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm text-center">
              {errorMessage}
            </div>
          )}

          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className={s.label}>Email</label>
                <input type="email" className={s.inputField} value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <label className={s.label}>Password</label>
                <div className="relative">
                  <input 
                    type={showPassword ? "text" : "password"} 
                    className={s.inputField} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    required 
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2">
                <button type="button" onClick={() => setView('forgot')} className={s.secondaryLink}>¿Olvidaste tu contraseña?</button>
                <button type="submit" disabled={loading} className={s.primaryButton}>
                  {loading ? <Loader2 className="animate-spin" size={18} /> : 'Ingresar'}
                </button>
              </div>
            </form>
          )}

          {view === 'forgot' && <ForgotPassword onBack={() => setView('login')} />}

          {view === 'update' && (
            <form onSubmit={handleUpdatePassword} className="space-y-5">
              {success ? (
                <div className="p-4 bg-green-50 border border-green-200 text-green-800 rounded-lg text-center font-medium">
                  ¡Contraseña establecida! Redirigiendo...
                </div>
              ) : (
                <>
                  <div className="relative">
                    <input 
                      type={showPassword ? "text" : "password"} 
                      placeholder="Nueva Contraseña" 
                      className={s.inputField} 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)} 
                      required 
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="relative">
                    <input 
                      type={showConfirmPassword ? "text" : "password"} 
                      placeholder="Confirmar Contraseña" 
                      className={s.inputField} 
                      value={confirmPassword} 
                      onChange={(e) => setConfirmPassword(e.target.value)} 
                      required 
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <button type="submit" disabled={loading} className={`${s.primaryButton} w-full mt-4`}>
                    {loading ? <Loader2 className="animate-spin" size={18} /> : 'Activar Cuenta'}
                  </button>
                </>
              )}
            </form>
          )}
        </div>
      </div>
      <div className={s.imageSection}>
        <img src={authImg} alt="MSC Background" className={s.heroImage} />
      </div>
    </div>
  );
};

export default Login;